import Box from '@mui/material/Box';
import ResourceApiService from '../api/ResourceApiService';
import { useEffect, useMemo, useState } from 'react';
import {
    Backdrop,
    Chip,
    Divider,
    Grid,
    Paper,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import ResourcePreview from './ResourceComponents/ResourcePreview';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import ConfirmDeleteDialog from './Modal/ConfirmDelete';
import EditResourceModal from './Modal/Resources/EditResourceModal';
import ResourceDto from '../dtos/Resources/ResourceDto';
import CircularProgress from '@mui/material/CircularProgress';
import { useSelector } from 'react-redux';
import AuthUtil from './Utils/AuthUtil';
import OrgApiService from '../api/OrgApiService';
import Typography from '@mui/material/Typography';

export default function Resources(props) {
    const { setSnackbarProperties } = props;

    const[resources, setResources] = useState([]);
    const[originalResources, setOriginalResources] = useState([]);
    const[topics, setTopics] = useState([]);
    const[types, setTypes] = useState([]);
    const[typeFilter, setTypeFilter] = useState('');
    const[topicFilter, setTopicFilter] = useState('');
    const[showDeleteDialog, setShowDeleteDialog] = useState(false);
    const[selectedResource, setSelectedResource] = useState();
    const[showEditDialog, setShowEditDialog] = useState(false);
    const[selectableOrgs, setSelectableOrgs] = useState([]);
    const[loading, setLoading] = useState(false);
    const[groupedResources, setGroupedResources] = useState(undefined);

    const resourceApiService = new ResourceApiService();
    const organizationApiService = new OrgApiService();

    const userToken = useSelector((state) => state.correcttrack.userToken);
    const isSuperAdmin = useMemo(() => AuthUtil.isSuperAdmin(userToken), [userToken]);
    const isAdmin = useMemo(() => AuthUtil.isAdmin(userToken), [userToken]);

    function applyFiltersToGroupedResources(allResources, topicFilter, typeFilter) {
        let filteredResources = allResources;

        if (topicFilter) {
            filteredResources = filteredResources.filter((resource) => {
                return resource.resourceTopics.filter(topic => topic.name === topicFilter).length > 0;
            });
        }

        if (typeFilter) {
            filteredResources = filteredResources.filter((resource) => {
                return resource.resourceType === typeFilter;
            });
        }

        const processedGroupedResources = {
            public: filteredResources.filter(r => r.isPublic),
            byOrg: {},
            unsorted: filteredResources.filter(r => !r.isPublic && r.ctOrgs.length === 0),
        };

        filteredResources.forEach((r) => {
            if (!r.isPublic && r.ctOrgs.length > 0) {
                r.ctOrgs.forEach((org) => {
                    if (!processedGroupedResources.byOrg[org.ogName]) {
                        processedGroupedResources.byOrg[org.ogName] = [];
                    }
                    processedGroupedResources.byOrg[org.ogName].push(r);
                });
            }
        });

        return processedGroupedResources;
    }
    function applyTopicFilter(applyFilter) {
        if(applyFilter === topicFilter) {
            setTopicFilter('');
            setGroupedResources(applyFiltersToGroupedResources(originalResources, '', typeFilter));
        } else {
            setTopicFilter(applyFilter);
            setGroupedResources(applyFiltersToGroupedResources(originalResources, applyFilter, typeFilter));
        }
    }

    function applyTypeFilter(applyFilter) {
        if(applyFilter === typeFilter) {
            setTypeFilter('');
            setGroupedResources(applyFiltersToGroupedResources(originalResources, topicFilter, ''));
        } else {
            setTypeFilter(applyFilter);
            setGroupedResources(applyFiltersToGroupedResources(originalResources, topicFilter, applyFilter));
        }
    }

    async function onDeleteClick(value) {
        if (!value) {
            setShowDeleteDialog(false);
            return;
        }

        if (selectedResource) {
            const deletedResource = await resourceApiService.deleteResource(selectedResource.rsId);
            if (deletedResource && deletedResource.success) {
                setSnackbarProperties('Deleted Resource', 'warning');
                await loadResources();
            }
        }

        setSelectedResource(new ResourceDto());
        setShowDeleteDialog(false);
    }

    function confirmDeleteResource(resource) {
        setSelectedResource(resource);
        setShowDeleteDialog(true);
    }

    function editResource(resource) {
        setSelectedResource(resource);
        setShowEditDialog(true);
    }

    function createNewResource() {
        setSelectedResource(new ResourceDto());
        setShowEditDialog(true);
    }

    function cancelEdit() {
        setShowEditDialog(false);
        setSelectedResource(new ResourceDto());
        loadResources();
    }

    function getNumberOfItemsWithTopic(topicName) {
        return originalResources.filter((value) => value.resourceTopics.some((topic) => topic.name === topicName)).length;
    }

    function getNumberOfItemsWithType(typeName) {
        return originalResources.filter((value) => value.resourceType === typeName).length;
    }

    async function loadResources() {
        setLoading(true);

        const [
            resourcesResponse,
            topicsResponse,
            typesResponse,
            basicOrgsResponse
        ] = await Promise.all([
            resourceApiService.getResources(),
            resourceApiService.getTopics(),
            resourceApiService.getTypes(),
            organizationApiService.getSelectableOrgs()
        ]);

        if(resourcesResponse.success) {
            setResources(resourcesResponse.data);
            setOriginalResources(resourcesResponse.data);
        } else {
            setSnackbarProperties('Error retrieving resources: ' + resourcesResponse.message, 'error');
        }

        if(topicsResponse.success) {
            setTopics(topicsResponse.data);
        } else {
            setSnackbarProperties('Error retrieving topics: ' + topicsResponse.message, 'error');
        }

        if(typesResponse.success) {
            setTypes(typesResponse.data);
        } else {
            setSnackbarProperties('Error retrieving types: ' + typesResponse.message, 'error');
        }

        if (basicOrgsResponse.success) {
            setSelectableOrgs(basicOrgsResponse.data);
        } else {
            setSnackbarProperties('Error retrieving orgs: ' + basicOrgsResponse.message, 'error');
        }

        const processedGroupedResources = {
            public: resourcesResponse.data.filter(r => r.isPublic),
            byOrg: {},
            unsorted: resourcesResponse.data.filter(r => !r.isPublic && r.ctOrgs.length === 0),
        };

        resourcesResponse.data.forEach((r) => {
            if (!r.isPublic && r.ctOrgs.length > 0) {
                r.ctOrgs.forEach((org) => {
                    if (!processedGroupedResources.byOrg[org.ogName]) {
                        processedGroupedResources.byOrg[org.ogName] = [];
                    }
                    processedGroupedResources.byOrg[org.ogName].push(r);
                });
            }
        });

        setGroupedResources(processedGroupedResources);

        setLoading(false);
    }

    useEffect(() => {
        loadResources();
    }, []);
    return (
        <Box>
            <h1>Resources</h1>
            {isSuperAdmin
                && <Button variant={'contained'} onClick={createNewResource}>Add New Resource</Button>
            }
            <br/><br/>
            {loading ? <Backdrop open={loading}><CircularProgress /></Backdrop> :
                resources.length === 0 ? (
                    <Box sx={{ textAlign: 'center', mt: 4 }}>
                        <Typography variant="h6" color="text.secondary">
                            No resources available
                        </Typography>
                    </Box>
                ) : (
                <Grid container columnSpacing={0.5}>
                    {(topics.length > 0 || types.length > 0) && (
                        <Grid container item xs={2.5} direction={'column'} sx={{marginRight: '10px'}}>
                            {topics.length > 0 && (
                                <TableContainer component={Paper}>
                                    <Table>
                                        <TableHead sx={{background: '#4D5061'}}>
                                            <TableRow>
                                                <TableCell sx={{color: 'white'}}>Topic</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {topics.sort(function (a, b) { return a.rtSort - b.rtSort;}).map((topic) => {
                                                return <TableRow
                                                    key={topic.id}
                                                    className={topicFilter === topic.name ? 'resource-filter resource-filter-selected' : 'resource-filter'}
                                                    onClick={() => applyTopicFilter(topic.name)}
                                                >
                                                    <TableCell className={'table-flex'} sx={{display: 'flex'}}>
                                                        <p className={'left-aligned-text'}>{topic.name}</p>
                                                        <Chip className={'right-aligned-text'} variant={'outlined'} label={getNumberOfItemsWithTopic(topic.name)}/>
                                                    </TableCell>
                                                </TableRow>
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                            {types.length > 0 && (
                                <TableContainer component={Paper} sx={{marginTop: topics.length > 0 ? '10px' : '0'}}>
                                    <Table>
                                        <TableHead sx={{background: '#4D5061'}}>
                                            <TableRow>
                                                <TableCell sx={{color: 'white'}}>Types</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {types.map((type) => {
                                                return <TableRow
                                                    key={type}
                                                    className={typeFilter === type ? 'resource-filter resource-filter-selected' : 'resource-filter'}
                                                    onClick={() => applyTypeFilter(type)}
                                                >
                                                    <TableCell sx={{display: 'flex'}}>
                                                        <p className={'left-aligned-text'}>{type}</p>
                                                        <Chip className={'right-aligned-text'} variant={'outlined'} label={getNumberOfItemsWithType(type)} />
                                                    </TableCell>
                                                </TableRow>
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Grid>
                    )}
                    <Grid container item xs={(topics.length > 0 || types.length > 0) ? 9 : 12} spacing={3} >
                        <Grid item xs={12}>
                            {groupedResources &&
                                Object.entries(groupedResources.byOrg)
                                    .filter(([orgName, resources]) => resources.length > 0)
                                    .map(([orgName, resources]) => (
                                        <Grid item xs={12} key={orgName} sx={{ pl: 0 }}>
                                            <Divider
                                                sx={{
                                                    my: 6,
                                                    borderBottomWidth: 3,
                                                    borderColor: 'primary.main',
                                                }}
                                                textAlign="left"
                                            >
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    {orgName}
                                                </Typography>
                                            </Divider>

                                            <Grid container spacing={3}>
                                                {resources.map((resource) => (
                                                    <Grid item key={resource.rsId}>
                                                        <ResourcePreview
                                                            resource={resource}
                                                            confirmDeleteResource={confirmDeleteResource}
                                                            editResource={editResource}
                                                        />
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </Grid>
                                    ))}
                        </Grid>
                        {groupedResources?.public.length > 0 && (
                            <>
                                <Grid item xs={12}>
                                    <Divider
                                        sx={{
                                            my: 6,
                                            borderBottomWidth: 3,
                                            borderColor: 'primary.main',
                                            marginTop: '-10px',
                                        }}
                                        textAlign="left"
                                    >
                                        <Typography variant="subtitle2" color="text.secondary">Public</Typography>
                                    </Divider>
                                </Grid>
                                <Grid item xs={12}>
                                    <Grid container spacing={3}>
                                        {groupedResources.public.map((resource) => {
                                            return <Grid item key={resource.rsId}>
                                                <ResourcePreview
                                                    resource={resource}
                                                    confirmDeleteResource={confirmDeleteResource}
                                                    editResource={editResource}
                                                />
                                            </Grid>
                                        })}
                                    </Grid>
                                </Grid>
                            </>
                        )}
                        {groupedResources?.unsorted.length > 0 && (
                            <>
                                <Grid item xs={12}>
                                    <Divider
                                        sx={{
                                            my: 6,
                                            borderBottomWidth: 3,
                                            borderColor: 'primary.main',
                                        }}
                                        textAlign="left"
                                    >
                                        <Typography variant="subtitle2" color="text.secondary">Not public or set for Organization</Typography>
                                    </Divider>
                                </Grid>
                                <Grid item xs={12}>
                                    <Grid container spacing={3}>
                                        {groupedResources.unsorted.map((resource) => {
                                            return <Grid item key={resource.rsId}>
                                                <ResourcePreview
                                                    resource={resource}
                                                    confirmDeleteResource={confirmDeleteResource}
                                                    editResource={editResource}
                                                />
                                            </Grid>
                                        })}
                                    </Grid>
                                </Grid>
                            </>
                        )}

                    </Grid>
                </Grid>
                )
            }


            <ConfirmDeleteDialog onClose={onDeleteClick} open={showDeleteDialog} />
            <EditResourceModal
                resource={selectedResource}
                setResource={setSelectedResource}
                open={showEditDialog}
                setSnackbarProperties={setSnackbarProperties}
                types={types}
                topics={topics}
                selectableOrgs={selectableOrgs}
                resourceApiService={resourceApiService}
                cancel={cancelEdit}
            />
        </Box>
    )
}
